/*
  cc: zoom and pan inside Dawn's product lightbox.

  Dawn ships two zoom modes. "hover" attaches magnify.js to the PDP image; "lightbox"
  opens the media modal but attaches nothing to it, so the modal is only a larger,
  scrollable copy — which is not a zoom. This adds real zoom to that modal:

    - wheel / trackpad pinch     zoom about the pointer
    - drag                       pan once zoomed in
    - two-finger pinch           zoom on touch
    - double click / double tap  toggle between fit and 2.5x

  Resolution matters as much as the interaction. Dawn's `sizes` on the modal image
  resolves to roughly the viewport width, so the browser picks a candidate around
  1100px and then displays it slightly larger — soft before any zoom is applied.
  The first time a customer zooms past fit, this swaps in the full-resolution URL
  from data-zoom-src and drops srcset, so the browser cannot fall back to a smaller
  candidate. It is deferred until first zoom so the initial open stays fast.

  No library: the modal's focus trap, media switching and gallery sync all live in
  Dawn's product-modal element, and a drop-in lightbox would have to replace them.
*/
(() => {
  if (window.ccImageZoomLoaded) return;
  window.ccImageZoomLoaded = true;

  const MIN_SCALE = 1;
  const MAX_SCALE = 5;
  const DOUBLE_TAP_SCALE = 2.5;

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  class CCZoomableImage {
    constructor(img, container) {
      this.img = img;
      this.container = container;
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pointers = new Map();
      this.pinchDistance = null;
      this.hiResRequested = false;
      this.dragging = false;

      this.img.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
      this.img.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.img.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.img.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.img.addEventListener('pointercancel', this.onPointerUp.bind(this));
      this.img.addEventListener('dblclick', this.onDoubleClick.bind(this));
      this.apply();
    }

    /* Layout size, unaffected by the transform — the basis for pan bounds. */
    get baseWidth() {
      return this.img.offsetWidth;
    }
    get baseHeight() {
      return this.img.offsetHeight;
    }

    /* Swap to the full-resolution file. srcset and sizes are removed so the browser
       cannot re-select a smaller candidate once the element grows. */
    loadFullResolution() {
      if (this.hiResRequested) return;
      const src = this.img.dataset.zoomSrc;
      if (!src) return;
      this.hiResRequested = true;

      const preload = new Image();
      preload.onload = () => {
        this.img.removeAttribute('srcset');
        this.img.removeAttribute('sizes');
        this.img.src = src;
      };
      preload.src = src;
    }

    /* Zoom about a point, keeping whatever is under it stationary. A point sitting
       d away from the centre lands at d * k after scaling, so translating by
       d - d * k puts it back. */
    zoomAt(clientX, clientY, nextScale) {
      const target = clamp(nextScale, MIN_SCALE, MAX_SCALE);
      if (target === this.scale) return;

      const rect = this.img.getBoundingClientRect();
      const dx = clientX - (rect.left + rect.width / 2);
      const dy = clientY - (rect.top + rect.height / 2);
      const k = target / this.scale;

      this.tx += dx - dx * k;
      this.ty += dy - dy * k;
      this.scale = target;

      if (this.scale > 1) this.loadFullResolution();
      this.clampPan();
      this.apply();
    }

    /* Never let the image pull away from the edges of the viewport box. */
    clampPan() {
      if (this.scale <= 1) {
        this.tx = 0;
        this.ty = 0;
        return;
      }
      const box = this.container.getBoundingClientRect();
      const overflowX = Math.max(0, (this.baseWidth * this.scale - box.width) / 2);
      const overflowY = Math.max(0, (this.baseHeight * this.scale - box.height) / 2);
      this.tx = clamp(this.tx, -overflowX, overflowX);
      this.ty = clamp(this.ty, -overflowY, overflowY);
    }

    apply() {
      const zoomed = this.scale > 1;
      this.img.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
      this.img.classList.toggle('cc-zoomed', zoomed);
      this.img.classList.toggle('cc-zoom-dragging', zoomed && this.dragging);
      /* Only claim the gesture while zoomed, so an un-zoomed modal still scrolls
         and swipes normally on touch. */
      this.img.style.touchAction = zoomed ? 'none' : '';
      this.container.classList.toggle('cc-zoom-active', zoomed);
    }

    reset() {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pointers.clear();
      this.pinchDistance = null;
      this.dragging = false;
      this.img.style.transition = '';
      this.apply();
    }

    onWheel(event) {
      event.preventDefault();
      /* ctrlKey is how browsers report a trackpad pinch. */
      const intensity = event.ctrlKey ? 0.02 : 0.0015;
      const next = this.scale * Math.exp(-event.deltaY * intensity);
      this.img.style.transition = '';
      this.zoomAt(event.clientX, event.clientY, next);
    }

    onPointerDown(event) {
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 1 && this.scale > 1) {
        this.dragging = true;
        this.img.setPointerCapture(event.pointerId);
        this.img.style.transition = '';
        this.apply();
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      const previous = this.pointers.get(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (this.pinchDistance) {
          const midX = (a.x + b.x) / 2;
          const midY = (a.y + b.y) / 2;
          this.zoomAt(midX, midY, this.scale * (distance / this.pinchDistance));
        }
        this.pinchDistance = distance;
        return;
      }

      if (!this.dragging || this.scale <= 1) return;
      event.preventDefault();
      this.tx += event.clientX - previous.x;
      this.ty += event.clientY - previous.y;
      this.clampPan();
      this.apply();
    }

    onPointerUp(event) {
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinchDistance = null;
      if (this.pointers.size === 0) {
        this.dragging = false;
        this.apply();
      }
    }

    onDoubleClick(event) {
      event.preventDefault();
      this.img.style.transition = 'transform 0.25s ease';
      if (this.scale > 1) {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.apply();
      } else {
        this.zoomAt(event.clientX, event.clientY, DOUBLE_TAP_SCALE);
      }
    }
  }

  const instances = new WeakMap();

  function attach(modal) {
    const container = modal.querySelector('.product-media-modal__content');
    if (!container) return;
    container.querySelectorAll('img[data-zoom-src]').forEach((img) => {
      if (instances.has(img)) return;
      instances.set(img, new CCZoomableImage(img, container));
    });
  }

  function resetAll(modal) {
    modal.querySelectorAll('img[data-zoom-src]').forEach((img) => {
      const instance = instances.get(img);
      if (instance) instance.reset();
    });
  }

  function init(root) {
    (root || document).querySelectorAll('product-modal').forEach((modal) => {
      attach(modal);
      if (modal.ccZoomObserved) return;
      modal.ccZoomObserved = true;
      /* Reset zoom whenever the modal closes, so reopening starts at fit rather
         than wherever the last customer left it. */
      new MutationObserver(() => {
        if (!modal.hasAttribute('open')) resetAll(modal);
      }).observe(modal, { attributes: true, attributeFilter: ['open'] });
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());
  if (document.readyState !== 'loading') init();
  /* The section re-renders on variant change, replacing the modal markup. */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
